import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true); // Enable by default
  const [volume, setVolume] = useState(0.3);
  const [audioAnalyser, setAudioAnalyser] = useState(null);
  const [dataArray, setDataArray] = useState(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);

  // Initialize audio context and analyser for visualizations
  const initializeAudioContext = () => {
    if (audioContextRef.current || !audioRef.current) return;

    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      analyserRef.current.fftSize = 32; // For 5 bars, we need 16 frequency bins
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
      
      setAudioAnalyser(analyserRef.current);
      setDataArray(dataArray);
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  };

  // Start background music
  const startMusic = async () => {
    if (!audioRef.current || !isEnabled) return;

    // Check user preference
    const userPaused = localStorage.getItem('backgroundMusicPaused') === 'true';
    if (userPaused) return;

    initializeAudioContext();
    
    // Resume audio context if it's suspended (required for auto-play)
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
      } catch (error) {
        console.warn('Could not resume audio context:', error);
      }
    }
    
    audioRef.current.volume = volume; // Set to target volume immediately
    
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.warn('Could not start background music (auto-play blocked):', error);
      // Store that we tried to auto-play but failed
      localStorage.setItem('autoPlayBlocked', 'true');
    }
  };

  // Pause music
  const pauseMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Resume music
  const resumeMusic = () => {
    if (!audioRef.current || !isEnabled) return;

    audioRef.current.play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch(error => {
        console.warn('Could not resume background music:', error);
      });
  };

  // Toggle play/pause
  const toggleMusic = () => {
    if (!isEnabled) return;

    if (isPlaying) {
      pauseMusic();
      localStorage.setItem('backgroundMusicPaused', 'true');
    } else {
      resumeMusic();
      localStorage.setItem('backgroundMusicPaused', 'false');
    }
  };

  // Pause music when other media is detected
  const pauseForOtherMedia = () => {
    if (isPlaying) {
      pauseMusic();
    }
  };

  // Enable background music (called when welcome video closes)
  const enableBackgroundMusic = () => {
    setIsEnabled(true);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const value = {
    isPlaying,
    isEnabled,
    volume,
    audioAnalyser,
    dataArray,
    audioRef,
    startMusic,
    pauseMusic,
    resumeMusic,
    toggleMusic,
    pauseForOtherMedia,
    enableBackgroundMusic,
    setVolume
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
      {/* Background audio element */}
      <audio
        ref={audioRef}
        loop
        preload="metadata"
        style={{ display: 'none' }}
      >
        <source src="/audio/background-music.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </AudioContext.Provider>
  );
}; 